import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { TextButton } from '@/components/common/text-button';
import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { OtpField } from '@/components/forms';
import { AUTH_ERROR_CODES, useOtpChallenge } from '@/features/auth';
import { ApiError, UnauthorizedError } from '@/lib/api/errors';

import { OTP_EXPIRY_CODES, PROFILE_COPY, PROFILE_ERROR_CODES } from '../constants';
import type { ProfileOtpDispatch } from '../schemas';

import otpModal from './otp-modal.module.scss';
import styles from './verification-code-step.module.scss';

type VerificationCodeStepProps = {
  /** The dispatch that opened this step (the send-otp response). */
  dispatch: ProfileOtpDispatch;
  /** 'Resend OTP' (phone, 117854 §5) or 'Resend code' (password, 117855 §5). Builds both the
   *  countdown ("… in mm:ss") and the available line ("… - N Attempts Remaining"). */
  resendLabel: string;
  /** Resolves once the caller has moved on: the success transition is the caller's, and it may catch
   *  its own codes first (ticket 04's 409 goes back to step 2). Rejects with whatever it did not
   *  handle; the step handles the branches every flow shares. */
  verify: (body: { referenceId: string; otp: string }) => Promise<void>;
  resend: (referenceId: string) => Promise<ProfileOtpDispatch>;
  /** The client countdown reached zero, or the server said the code session is over (grilling Q2). */
  onExpired: () => void;
  /** Cancel, ✕ or Escape. */
  onCancel: () => void;
};

type CodeForm = { otp: string };

const mmss = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

/** The shared "Enter the 6 Digit Code" screen: phone current (03), phone new (04), password (05). */
export function VerificationCodeStep({
  dispatch,
  resendLabel,
  verify,
  resend,
  onExpired,
  onCancel,
}: VerificationCodeStepProps) {
  const toast = useToast();
  const [referenceId, setReferenceId] = useState(dispatch.referenceId);
  const [rejected, setRejected] = useState<{ otp: string; message: string } | null>(null);
  const challenge = useOtpChallenge(dispatch);
  const resendMutation = useMutation({ mutationFn: resend });
  // No resolver: Verify is gated on length (§2c), and a resolver would re-validate on change after
  // the first submit and paint a "6-digit" error under a partial entry no AC asks for.
  const form = useForm<CodeForm>({ defaultValues: { otp: '' } });
  const otp = useWatch({ control: form.control, name: 'otp' }) ?? '';

  // §2e: an invalid-code error belongs to the digits it judged, so any edit drops it — reset during
  // render (the React-sanctioned adjustment), so retyping the same wrong code cannot resurrect it.
  if (rejected && rejected.otp !== otp) setRejected(null);
  const codeError = rejected?.message ?? null;
  const { setError, clearErrors } = form;
  useEffect(() => {
    if (codeError) setError('otp', { message: codeError });
    else clearErrors('otp');
  }, [codeError, setError, clearErrors]);

  useEffect(() => {
    if (challenge.expired) onExpired(); // §4a: no request, no toast
  }, [challenge.expired, onExpired]);

  /** The branches all three flows share. */
  const handleShared = (error: unknown) => {
    if (error instanceof ApiError && error.code && OTP_EXPIRY_CODES.includes(error.code)) {
      onExpired();
      return;
    }
    if (error instanceof UnauthorizedError) return; // session dead — sessionExpiry owns it (§14)
    if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.resendRateLimitExceeded) {
      toast.warning({ title: PROFILE_COPY.rateLimited, key: 'profile-otp' });
      return;
    }
    toast.error({ title: PROFILE_COPY.genericError, key: 'profile-otp' });
  };

  const onSubmit = async ({ otp: code }: CodeForm) => {
    try {
      await verify({ referenceId, otp: code });
    } catch (error) {
      if (error instanceof ApiError && error.code === AUTH_ERROR_CODES.OTP_INVALID) {
        setRejected({ otp: code, message: error.message }); // the server's per-flow copy (ADR-0008)
        return;
      }
      handleShared(error); // §3d: the digits stay — nothing resets the form
    }
  };

  const onResend = async () => {
    try {
      const next = await resendMutation.mutateAsync(referenceId);
      setReferenceId(next.referenceId); // §5c: the old reference now 404s
      challenge.onResend(next);
    } catch (error) {
      handleShared(error);
    }
  };

  // Countdown while cooling down; the control otherwise — disabled once no attempts remain (§5f).
  const showControl = challenge.canResend || challenge.attemptsRemaining === 0;

  return (
    <FormModal
      open
      form={form}
      onSubmit={onSubmit}
      onClose={onCancel}
      title={PROFILE_COPY.verificationCode.title}
      subtitle={
        <span className={otpModal.helper}>
          {PROFILE_COPY.verificationCode.helper(dispatch.maskedPhone.slice(-4))}
        </span>
      }
      width={456}
      className={otpModal.modal}
      submitText={PROFILE_COPY.verificationCode.verify}
      primaryDisabled={otp.length !== 6} // stays enabled after an invalid code (grilling Q8)
      maskClosable={false} // §8 names Cancel, ✕ and Escape; a stray click must not drop the code
    >
      <div className={styles.code}>
        <OtpField
          name="otp"
          control={form.control}
          length={6}
          label={PROFILE_COPY.verificationCode.label}
          required
          autoFocus
        />
        <div className={styles.resend}>
          {showControl ? (
            <TextButton
              htmlType="button"
              className={styles.resendButton}
              disabled={!challenge.canResend}
              loading={resendMutation.isPending}
              onClick={() => void onResend()}
            >
              <span className={styles.resendLead}>{resendLabel} - </span>
              {challenge.attemptsRemaining} Attempts Remaining
            </TextButton>
          ) : (
            <span className={styles.countdown}>
              {resendLabel} in {mmss(challenge.resendSecondsLeft)}
            </span>
          )}
        </div>
      </div>
    </FormModal>
  );
}
